import PageNavbar from "@/components/feature/PageNavbar";
import PageFooter from "@/components/feature/PageFooter";

const LAST_UPDATED = "July 13, 2026";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
      <PageNavbar />

      <section className="relative w-full overflow-hidden" style={{ padding: "120px 40px 60px" }}>
        <div className="mx-auto" style={{ maxWidth: 1440 }}>
          <div style={{ fontSize: 13, color: "#8A8A8A", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 20 }}>
            Legal
          </div>
          <h1 style={{ fontSize: "clamp(36px, 5vw, 72px)", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05, color: "#ECECEC", margin: 0, maxWidth: 900 }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: 15, color: "#8A8A8A", marginTop: 16 }}>
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      <div className="mx-auto" style={{ maxWidth: 900, padding: "0 40px 120px" }}>
        <div style={{ fontSize: 15, color: "#8A8A8A", lineHeight: 1.85 }}>
          <Section title="1. Introduction">
            <p style={{ marginBottom: 16 }}>
              This Privacy Policy explains how Intellivex LTD (&quot;Company&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) collects, uses, stores, and protects personal data when you use our website (the &quot;Site&quot;) and any related services (collectively, the &quot;Services&quot;).
            </p>
            <p style={{ marginBottom: 4, fontWeight: 600, color: "#ECECEC" }}>Registered Address:</p>
            <p style={{ marginBottom: 4 }}>50 Essex Street</p>
            <p style={{ marginBottom: 4 }}>London</p>
            <p style={{ marginBottom: 4 }}>WC2R 3JF</p>
            <p style={{ marginBottom: 16 }}>United Kingdom</p>
            <p style={{ marginBottom: 4 }}>Email: support@inntellivex.com</p>
            <p style={{ marginBottom: 16 }}>Phone: +44 7520 680012</p>
            <p style={{ marginBottom: 16 }}>
              We are committed to protecting your privacy and processing personal information in accordance with applicable data protection laws, including the UK General Data Protection Regulation (UK GDPR), where applicable.
            </p>
            <p>
              By accessing or using the Site, you acknowledge that you have read and understood this Privacy Policy.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p style={{ marginBottom: 16 }}>We may collect the following categories of information:</p>

            <SubTitle>a. Personal Information</SubTitle>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Name</li>
              <li style={{ marginBottom: 6 }}>Email address</li>
              <li style={{ marginBottom: 6 }}>Phone number (if voluntarily provided)</li>
              <li>Any information you submit through contact forms or other communications</li>
            </ul>

            <SubTitle>b. Technical and Usage Information</SubTitle>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>IP address</li>
              <li style={{ marginBottom: 6 }}>Browser type and version</li>
              <li style={{ marginBottom: 6 }}>Device type and operating system</li>
              <li style={{ marginBottom: 6 }}>Pages visited and interaction data</li>
              <li>Date and time of access</li>
            </ul>

            <SubTitle>c. Communication Information</SubTitle>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Messages submitted through contact forms or email</li>
              <li>Customer support requests and related correspondence</li>
            </ul>

            <SubTitle>d. Cookies and Similar Technologies</SubTitle>
            <p>
              We use cookies and similar technologies to improve the functionality, security, and performance of the Site. Please refer to Section 9 for additional information.
            </p>
          </Section>

          <Section title="3. How We Use Your Information">
            <p style={{ marginBottom: 16 }}>We use your information for the following purposes:</p>

            <SubTitle>a. Providing Our Services</SubTitle>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Responding to inquiries and requests</li>
              <li style={{ marginBottom: 6 }}>Operating, maintaining, and improving the Site</li>
              <li>Providing requested information, products, or services</li>
            </ul>

            <SubTitle>b. Improving the Site</SubTitle>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Understanding how visitors use the Site</li>
              <li style={{ marginBottom: 6 }}>Analyzing performance and functionality</li>
              <li>Enhancing user experience</li>
            </ul>

            <SubTitle>c. Communication</SubTitle>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Responding to customer inquiries</li>
              <li style={{ marginBottom: 6 }}>Sending important service-related notices</li>
              <li>Providing customer support</li>
            </ul>

            <SubTitle>d. Legal Compliance</SubTitle>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Complying with applicable legal obligations</li>
              <li style={{ marginBottom: 6 }}>Preventing fraud, abuse, or unauthorized use</li>
              <li>Enforcing our Terms of Use</li>
            </ul>

            <SubTitle>e. Marketing (Where Applicable)</SubTitle>
            <p style={{ marginBottom: 16 }}>
              With your consent, we may send promotional communications, updates, or marketing materials.
            </p>
            <p>You may withdraw your consent or unsubscribe at any time.</p>
          </Section>

          <Section title="4. Legal Basis for Processing">
            <p style={{ marginBottom: 16 }}>
              Depending on the circumstances, we process personal data based on one or more of the following legal grounds:
            </p>
            <ul style={{ listStyle: "disc", paddingLeft: 20 }}>
              <li style={{ marginBottom: 6 }}>Your consent</li>
              <li style={{ marginBottom: 6 }}>Our legitimate business interests</li>
              <li style={{ marginBottom: 6 }}>Compliance with legal obligations</li>
              <li>Performance of a contract or responding to your request prior to entering into a contract</li>
            </ul>
          </Section>

          <Section title="5. Sharing of Information">
            <p style={{ marginBottom: 16 }}>We may share your information with:</p>

            <SubTitle>a. Service Providers</SubTitle>
            <p style={{ marginBottom: 16 }}>Trusted third-party providers that assist us with services such as:</p>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Website hosting</li>
              <li style={{ marginBottom: 6 }}>Email communications</li>
              <li style={{ marginBottom: 6 }}>Analytics</li>
              <li>Customer support</li>
            </ul>
            <p style={{ marginBottom: 16 }}>
              These providers are contractually required to safeguard your information and process it only on our behalf.
            </p>

            <SubTitle>b. Legal Requirements</SubTitle>
            <p style={{ marginBottom: 16 }}>
              We may disclose information where required by law or when necessary to protect our legal rights, users, or property.
            </p>

            <SubTitle>c. Business Transfers</SubTitle>
            <p style={{ marginBottom: 16 }}>
              If our business is involved in a merger, acquisition, restructuring, or sale of assets, your information may be transferred as part of that transaction, subject to appropriate safeguards.
            </p>

            <SubTitle>d. With Your Consent</SubTitle>
            <p style={{ marginBottom: 16 }}>
              We may share your information with third parties where you have expressly authorized us to do so.
            </p>
            <p style={{ fontWeight: 600, color: "#ECECEC" }}>We do not sell your personal information to third parties.</p>
          </Section>

          <Section title="6. Data Retention">
            <p style={{ marginBottom: 16 }}>We retain personal information only for as long as reasonably necessary to:</p>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Fulfill the purposes described in this Privacy Policy</li>
              <li style={{ marginBottom: 6 }}>Comply with legal obligations</li>
              <li style={{ marginBottom: 6 }}>Resolve disputes</li>
              <li>Enforce our legal agreements</li>
            </ul>
            <p>
              Retention periods may vary depending on the nature of the information collected and applicable legal requirements.
            </p>
          </Section>

          <Section title="7. Data Security">
            <p style={{ marginBottom: 16 }}>
              We implement appropriate technical and organizational measures designed to protect your personal information, including:
            </p>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Secure storage and encrypted transmission where appropriate</li>
              <li style={{ marginBottom: 6 }}>Access controls and authentication measures</li>
              <li>Continuous monitoring against unauthorized access or misuse</li>
            </ul>
            <p>
              While we take reasonable steps to protect your information, no method of electronic storage or transmission can be guaranteed to be completely secure.
            </p>
          </Section>

          <Section title="8. Your Privacy Rights">
            <p style={{ marginBottom: 16 }}>Subject to applicable data protection laws, you may have the right to:</p>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Access your personal information</li>
              <li style={{ marginBottom: 6 }}>Correct inaccurate or incomplete information</li>
              <li style={{ marginBottom: 6 }}>Request deletion of your personal information</li>
              <li style={{ marginBottom: 6 }}>Restrict or object to certain processing activities</li>
              <li style={{ marginBottom: 6 }}>Request a copy of your data in a portable format</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
            <p style={{ marginBottom: 4 }}>To exercise any of these rights, please contact us at:</p>
            <p style={{ fontWeight: 600, color: "#ECECEC" }}>support@inntellivex.com</p>
          </Section>

          <Section title="9. Cookies">
            <p style={{ marginBottom: 16 }}>
              We use cookies and similar technologies to improve the functionality and performance of the Site.
            </p>
            <p style={{ marginBottom: 16 }}>The cookies we use may include:</p>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}><strong style={{ color: "#ECECEC" }}>Essential Cookies</strong> – required for the proper operation of the Site.</li>
              <li style={{ marginBottom: 6 }}><strong style={{ color: "#ECECEC" }}>Analytics Cookies</strong> – help us understand how visitors interact with the Site.</li>
              <li><strong style={{ color: "#ECECEC" }}>Functional Cookies</strong> – remember your preferences and improve usability.</li>
            </ul>
            <p>
              You may manage or disable cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of the Site.
            </p>
          </Section>

          <Section title="10. International Data Transfers">
            <p style={{ marginBottom: 16 }}>
              Your personal information may be processed or stored outside your country of residence.
            </p>
            <p>
              Where required by applicable law, we implement appropriate safeguards to ensure your personal information remains protected during international transfers.
            </p>
          </Section>

          <Section title="11. Children&apos;s Privacy">
            <p style={{ marginBottom: 16 }}>
              The Site and Services are not intended for individuals under the age of 16.
            </p>
            <p>
              We do not knowingly collect personal information from children. If we become aware that such information has been collected, we will take reasonable steps to delete it promptly.
            </p>
          </Section>

          <Section title="12. Changes to this Privacy Policy">
            <p style={{ marginBottom: 16 }}>
              We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or business operations.
            </p>
            <p style={{ marginBottom: 16 }}>
              Any updates will be posted on this page with a revised effective date.
            </p>
            <p>
              Your continued use of the Site following any updates constitutes your acceptance of the revised Privacy Policy.
            </p>
          </Section>

          <Section title="13. Contact Information">
            <p style={{ marginBottom: 8 }}>If you have any questions regarding this Privacy Policy or our data processing practices, please contact us:</p>
            <p style={{ marginBottom: 4 }}>Intellivex LTD</p>
            <p style={{ marginBottom: 4 }}>50 Essex Street</p>
            <p style={{ marginBottom: 4 }}>London</p>
            <p style={{ marginBottom: 4 }}>WC2R 3JF</p>
            <p style={{ marginBottom: 4 }}>United Kingdom</p>
            <p style={{ marginBottom: 4 }}>Email: support@inntellivex.com</p>
            <p>Phone: +44 7520 680012</p>
          </Section>
        </div>
      </div>

      <PageFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 48 }}>
      <h2 style={{ fontSize: 22, fontWeight: 600, color: "#ECECEC", letterSpacing: "-0.02em", marginBottom: 16 }}>
        {title}
      </h2>
      <div>{children}</div>
    </div>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{ fontSize: 16, fontWeight: 600, color: "#D1D1D1", marginBottom: 12, marginTop: 8 }}>
      {children}
    </h3>
  );
}